import _ from '@/common/helpers';
import FieldIndexer from '@/common/spec/field-indexer';
import getFieldsFromPathways from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';

const arrayLineageStep = '0';

/**
 * Generate an index of all placeholder values available based on the pathways provided.
 */
export default class PlaceholderIndexer extends FieldIndexer {
    static Delimiter = '.';

    constructor(customFieldsStore, pathways, extraPlaceholders = []) {
        super({
            helpers: _,
            fields: getFieldsFromPathways(pathways, customFieldsStore, {
                delimiter: PlaceholderIndexer.Delimiter,
                trim: false,
            }),
        });
        this.buildExtraPlaceholderIndex(extraPlaceholders);
    }

    buildIndex(pathways, helpers) {
        this.helpers = helpers;
        const parse = (root, fields, lineage) => {
            const keys = Object.keys(fields);
            keys.forEach((prop) => {
                const field = fields[prop];
                const fullPath = `${root}${PlaceholderIndexer.Delimiter}${prop}`;
                if (field.$nested) {
                    if (field.$array) {
                        // add a `n=0` step for array placeholders
                        parse(`${fullPath}.${arrayLineageStep}`, field, lineage.concat(prop, arrayLineageStep));
                    } else {
                        // parse nested properties from a new root
                        parse(fullPath, field, lineage.concat(prop));
                    }
                } else if (!FieldIndexer.isSpecialKey(prop)) {
                    const groupLineage = lineage.map(step => this.resolveLabel(step, helpers));
                    const label = this.resolveLabel(prop, helpers);
                    this.index[fullPath] = {
                        field,
                        fullPath,
                        group: root,
                        groupLineage,
                        label,
                        lineage: groupLineage.concat(label),
                        prop,
                    };
                }
            });
        };
        // start parsing at the root recursively
        Object.keys(pathways)
            .forEach((group) => {
                const {fields, lineage} = pathways[group];
                parse(group, fields, lineage);
            });
        // cache index
        this.indexKeys = Object.keys(this.index);
    }

    /**
     * If the event spec has extra placeholders defined, inject them in the index arbitrarily and
     * allow the UI to use them. This will override existing definitions.
     * @param extraPlaceholders
     */
    buildExtraPlaceholderIndex(extraPlaceholders) {
        if (extraPlaceholders.length) {
            extraPlaceholders.forEach((placeholder) => {
                this.index[placeholder] = {
                    fullPath: placeholder,
                };
            });
        }
    }

    /**
     * Search for perfect and partial matches in the index.
     * Each word provided in the query triggers its own search.
     * @param {string} rawQuery
     * @return {*}
     */
    find(rawQuery = '') {
        const words = rawQuery.trim()
            .toLowerCase()
            .replace(/\{|}/g, '')
            .split(/\s/)
            .filter(v => !this.helpers.isBlank(v));
        if (words.length) {
            const results = {};
            words.forEach((word) => {
                if (Reflect.has(this.index, word)) {
                    results[word] = true;
                } else {
                    const matches = this.indexKeys
                        .filter(v => new RegExp(word, 'i').test(v));
                    if (matches.length) {
                        matches.forEach((v) => {
                            results[v] = true;
                        });
                    }
                }
            });
            const keys = Object.keys(results);
            if (keys.length) {
                return keys;
            }
        }
        return null;
    }
}
